import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TestTube, Package, ChevronRight, Pill } from 'lucide-react';
import api from '../api/axios';
import { useCart } from '../context/CartContext';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';

const MedicalTestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { cart, addMedicalTest, removeItem, isAuthenticated } = useCart();

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedTests, setRelatedTests] = useState([]);
  const [suggestedPackages, setSuggestedPackages] = useState([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);

  useEffect(() => {
    // Load selected tests from localStorage
    try {
      const storedTests = localStorage.getItem('selectedTests');
      if (storedTests) {
        setSelectedTests(JSON.parse(storedTests));
      }
    } catch (err) {
      console.error('Error loading selected tests:', err);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchTestDetail();
      fetchSuggestions();
    }
  }, [id]);

  const fetchTestDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/medical-tests/${id}/`);
      const testData = response.data.data || response.data;
      setTest(testData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch test details');
      console.error('Error fetching test detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const [relatedRes, packagesRes] = await Promise.all([
        api.get(`/suggestions/related-tests/${id}/`).catch(() => null),
        api.get(`/suggestions/packages-for-test/${id}/`).catch(() => null)
      ]);

      if (relatedRes?.data?.success) {
        setRelatedTests(relatedRes.data.related_tests || []);
      }
      if (packagesRes?.data?.success) {
        setSuggestedPackages(packagesRes.data.suggested_packages || []);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  };

  const isSelected = () => {
    // Check backend cart first
    const isInCart = cart?.test_items?.some(item => item.test?.id === parseInt(id));
    // Also check local state
    const isInLocalState = selectedTests.some(selectedTest => selectedTest.id === parseInt(id));
    return isInCart || isInLocalState;
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (!test) return;

    // Check if test is already in cart
    const cartItem = cart?.test_items?.find(item => item.test?.id === parseInt(id));

    // If already in cart, just navigate to medical tests booking page
    if (cartItem) {
      navigate('/medical-tests/booking');
      return;
    }

    // Otherwise, add to cart
    setIsAddingToCart(true);

    try {
      // Add test to cart via API
      await addMedicalTest(parseInt(id), 1);

      // Update local state
      const newSelectedTests = [...selectedTests, test];
      setSelectedTests(newSelectedTests);
      localStorage.setItem('selectedTests', JSON.stringify(newSelectedTests));

      // Don't navigate automatically - let user stay on detail page
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(error.message || 'Failed to add to cart');
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent shadow-lg"></div>
            <div className="absolute inset-0 rounded-full h-16 w-16 border-4 border-gray-200"></div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !test) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center max-w-md">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-red-800 mb-4">{error || 'Test not found'}</h3>
            <p className="text-red-600 mb-8">Sorry, we couldn't load the test details. Please try again.</p>
            <button
              onClick={() => navigate('/medical-tests')}
              className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Go Back to Tests
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/medical-tests')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors mb-4"
          >
            <ArrowLeft size={20} />
            Back to Medical Tests
          </button>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Test Details */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                {/* Header */}
                <div className="p-8 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20"></div>
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>

                  <div className="relative z-10">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                        <TestTube className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h1 className="text-4xl font-bold mb-2">{test.title}</h1>
                        <p className="text-blue-100">Comprehensive Medical Test</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-6">
                      {test.discount_percentage > 0 ? (
                        <>
                          <span className="text-5xl font-bold text-white">
                            ₹{test.offer_price || test.effective_price}
                          </span>
                          <span className="text-xl text-blue-200 line-through">
                            ₹{test.price}
                          </span>
                          <span className="bg-white text-red-600 text-sm px-4 py-2 rounded-full font-bold shadow-lg">
                            {test.discount_percentage}% OFF
                          </span>
                        </>
                      ) : (
                        <span className="text-5xl font-bold text-white">
                          ₹{test.price}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-2 h-10 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full mr-4"></div>
                    <h2 className="text-2xl font-bold text-gray-900">Test Description</h2>
                  </div>
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                      {test.description}
                    </p>
                  </div>
                </div>

                {/* Features */}
                <div className="p-8 bg-gradient-to-br from-gray-50 to-blue-50">
                  <div className="flex items-center mb-8">
                    <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full mr-4"></div>
                    <h3 className="text-2xl font-bold text-gray-900">Test Features & Benefits</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      { icon: "🏠", title: "Home Collection", desc: "Sample collected at your doorstep by trained professionals", bgColor: "bg-blue-100" },
                      { icon: "⚡", title: "Fast Results", desc: "Get your reports within 24-48 hours via email", bgColor: "bg-green-100" },
                      { icon: "📱", title: "Digital Reports", desc: "Access and download reports online anytime, anywhere", bgColor: "bg-purple-100" },
                      { icon: "✅", title: "Quality Assured", desc: "NABL accredited lab testing with highest standards", bgColor: "bg-orange-100" }
                    ].map((feature, index) => (
                      <div key={index} className="group p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="flex items-start space-x-4">
                          <div className={`w-14 h-14 ${feature.bgColor} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                            <span className="text-2xl">{feature.icon}</span>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-2 text-lg">{feature.title}</h4>
                            <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Action Card */}
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 sticky top-6">
                <div className="flex items-center mb-6">
                  <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full mr-4"></div>
                  <h3 className="text-xl font-bold text-gray-900">Book This Test</h3>
                </div>

                <div className="space-y-6">
                  <div className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-semibold">Test Price:</span>
                      <div className="text-right">
                        {test.discount_percentage > 0 ? (
                          <div>
                            <span className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                              ₹{test.offer_price || test.effective_price}
                            </span>
                            <div className="text-sm text-gray-500 line-through">
                              ₹{test.price}
                            </div>
                            <div className="text-xs text-green-600 font-semibold">
                              You save ₹{parseFloat(test.price) - parseFloat(test.offer_price || test.effective_price)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-3xl font-bold text-gray-900">
                            ₹{test.price}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    disabled={isAddingToCart}
                    className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:transform-none disabled:cursor-not-allowed ${
                      isSelected()
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white'
                        : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
                    } ${isAddingToCart ? 'opacity-75' : ''}`}
                  >
                    {isAddingToCart ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Adding to Cart...
                      </span>
                    ) : isSelected() ? (
                      <span className="flex items-center justify-center">
                        <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Go to Cart
                      </span>
                    ) : (
                      'Add to Cart & Book Now'
                    )}
                  </button>

                  {selectedTests.length > 0 && (
                    <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <p className="text-sm text-blue-800 font-semibold mb-2">
                        🛒 {selectedTests.length} test{selectedTests.length > 1 ? 's' : ''} in cart
                      </p>
                      {selectedTests.length > 0 && !isSelected() && (
                        <button
                          onClick={() => navigate('/medical-tests/booking')}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium underline"
                        >
                          View Cart
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Service Features */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-2 h-6 bg-gradient-to-b from-blue-600 to-purple-600 rounded-full mr-3"></div>
                  <h3 className="text-lg font-bold text-blue-900">Why Choose Us?</h3>
                </div>
                <div className="space-y-4">
                  {[
                    { icon: "🚚", text: "Free home sample collection" },
                    { icon: "📍", text: "Service within 30km radius" },
                    { icon: "📧", text: "Email & SMS notifications" },
                    { icon: "📄", text: "Digital invoice & reports" }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-blue-800 bg-white/50 rounded-lg p-3">
                      <span className="text-lg mr-3">{feature.icon}</span>
                      <span className="font-medium">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Suggested Packages */}
          {suggestedPackages.length > 0 && (
            <div className="mt-20">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-4 flex items-center justify-center gap-3">
                  <Package className="w-8 h-8 text-purple-600" />
                  Recommended Health Packages
                </h2>
                <p className="text-gray-600">Get comprehensive health checkups at great value</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {suggestedPackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className="group bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/test-package/${pkg.id}`)}
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Package className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                      {pkg.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2 leading-relaxed">
                      {pkg.description}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-purple-600 font-medium mb-4">
                      <TestTube className="w-4 h-4" />
                      <span>{pkg.test_count || pkg.tests_count} Tests Included</span>
                    </div>
                    <div className="flex justify-between items-center border-t pt-4">
                      {pkg.discount_percentage > 0 ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-green-600">
                            ₹{pkg.offer_price}
                          </span>
                          <span className="text-sm text-gray-500 line-through">
                            ₹{pkg.total_price}
                          </span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">
                          ₹{pkg.total_price}
                        </span>
                      )}
                      <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center group-hover:translate-x-1 transition-transform">
                        View Details
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related Tests */}
          {relatedTests.length > 0 && (
            <div className="mt-20">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-4 flex items-center justify-center gap-3">
                  <TestTube className="w-8 h-8 text-blue-600" />
                  Related Tests
                </h2>
                <p className="text-gray-600">You might also be interested in these tests</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {relatedTests.map((relatedTest) => (
                  <div
                    key={relatedTest.id}
                    className="group bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/medical-test/${relatedTest.id}`)}
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <TestTube className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {relatedTest.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                      {relatedTest.description}
                    </p>
                    <div className="flex justify-between items-center">
                      {relatedTest.discount_percentage > 0 ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-green-600">
                            ₹{relatedTest.offer_price || relatedTest.effective_price}
                          </span>
                          <span className="text-sm text-gray-500 line-through">
                            ₹{relatedTest.price}
                          </span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">
                          ₹{relatedTest.price}
                        </span>
                      )}
                      <button className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center group-hover:translate-x-1 transition-transform">
                        View Details
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MedicalTestDetailPage;
